import React, { useState, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Suche } from '../icons';
import './SearchWithSuggestions.css';

/**
 * Wiederverwendbare Suchleiste mit Autocomplete-Vorschlägen.
 *
 * Props:
 *  - id:          HTML-ID für das Input-Feld
 *  - label:       Text für das Label-Element
 *  - value:       aktueller Suchbegriff (controlled)
 *  - onChange:    (newValue: string) => void
 *  - suggestions: Array von { id, name } Objekten
 *  - suggestionsId: ID für die Suggestions-Liste (aria-controls)
 *
 * Ref: Exposes { focus() } to parent via forwardRef.
 */
const SearchWithSuggestions = forwardRef(function SearchWithSuggestions({ id, label, value, onChange, suggestions = [], suggestionsId }, ref) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedSuggestion, setHighlightedSuggestion] = useState(-1);
  const inputRef = useRef(null);

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
  }));

  const listId = suggestionsId || `${id}-suggestions`;

  const handleChange = useCallback((e) => {
    onChange(e.target.value);
    setShowSuggestions(true);
    setHighlightedSuggestion(-1);
  }, [onChange]);

  const selectSuggestion = useCallback((name) => {
    onChange(name);
    setShowSuggestions(false);
    setHighlightedSuggestion(-1);
  }, [onChange]);

  const clearSearch = useCallback(() => {
    onChange('');
    setShowSuggestions(false);
    setHighlightedSuggestion(-1);
    inputRef.current?.focus();
  }, [onChange]);

  const handleKeyDown = useCallback((e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      setHighlightedSuggestion(h => Math.min(h + 1, suggestions.length - 1));
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      setHighlightedSuggestion(h => Math.max(h - 1, 0));
      e.preventDefault();
    } else if (e.key === 'Enter' && highlightedSuggestion >= 0) {
      selectSuggestion(suggestions[highlightedSuggestion].name);
      setTimeout(() => inputRef.current?.blur(), 0);
      e.preventDefault();
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setHighlightedSuggestion(-1);
    }
  }, [showSuggestions, suggestions, highlightedSuggestion, selectSuggestion]);

  return (
    <div className="search-wrapper">
      <label className="search-label" htmlFor={id}>
        {label}
      </label>
      <span className="search-icon-left"><Suche /></span>
      <input
        id={id}
        type="text"
        className="search-input"
        value={value}
        ref={inputRef}
        autoComplete="off"
        aria-autocomplete="list"
        aria-controls={listId}
        aria-activedescendant={
          highlightedSuggestion >= 0 && showSuggestions
            ? `${id}-suggestion-${highlightedSuggestion}`
            : undefined
        }
        onChange={handleChange}
        onFocus={() => setShowSuggestions(true)}
        onKeyDown={handleKeyDown}
      />
      {value && (
        <span
          className="search-clear"
          tabIndex={0}
          role="button"
          aria-label="Suche zurücksetzen"
          title="Suche zurücksetzen"
          onClick={clearSearch}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') clearSearch();
          }}
        >
          ✖
        </span>
      )}
      {showSuggestions && suggestions.length > 0 && (
        <div className="search-suggestions" id={listId} role="listbox">
          {suggestions.map((s, idx) => (
            <div
              key={s.id}
              id={`${id}-suggestion-${idx}`}
              className={`search-suggestion${highlightedSuggestion === idx ? ' highlighted' : ''}`}
              role="option"
              aria-selected={highlightedSuggestion === idx}
              tabIndex={-1}
              onMouseDown={() => selectSuggestion(s.name)}
              onMouseEnter={() => setHighlightedSuggestion(idx)}
              style={highlightedSuggestion === idx ? { background: '#1b1c3b', color: 'white' } : {}}
            >
              {s.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

export default SearchWithSuggestions;
